import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { testimonials } from '../data/mock';
import { Quote, ChevronLeft, ChevronRight } from 'lucide-react';

const TestimonialsSection = () => {
  const scrollContainerRef = useRef(null);
  const sectionRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScroll();
    const ref = scrollContainerRef.current;
    if (ref) {
      ref.addEventListener('scroll', checkScroll);
      return () => ref.removeEventListener('scroll', checkScroll);
    }
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = scrollContainerRef.current.clientWidth * 0.8;
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section className="py-24 bg-slate-50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
            Lo que dicen nuestros asistentes
          </h2>
          <p className="text-lg text-slate-600">Experiencias de quienes ya participaron</p>
        </div>

        <div ref={sectionRef} className="relative opacity-0">
          {/* Left arrow */}
          <Button
            onClick={() => scroll('left')}
            disabled={!canScrollLeft}
            className={`absolute left-2 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white hover:bg-slate-100 text-slate-900 shadow-xl border border-slate-200 p-0 transition-all duration-300 ${
              !canScrollLeft ? 'opacity-30 cursor-not-allowed' : 'hover:scale-110'
            }`}
            aria-label="Anterior"
          >
            <ChevronLeft className="w-6 h-6" />
          </Button>

          {/* Right arrow */}
          <Button
            onClick={() => scroll('right')}
            disabled={!canScrollRight}
            className={`absolute right-2 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white hover:bg-slate-100 text-slate-900 shadow-xl border border-slate-200 p-0 transition-all duration-300 ${
              !canScrollRight ? 'opacity-30 cursor-not-allowed' : 'hover:scale-110'
            }`}
            aria-label="Siguiente"
          >
            <ChevronRight className="w-6 h-6" />
          </Button>

          <div
            ref={scrollContainerRef}
            className="overflow-x-auto pb-8 scrollbar-hide scroll-smooth"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            <div className="flex space-x-6 px-4">
              {testimonials.map((testimonial) => (
                <Card
                  key={testimonial.id}
                  className="flex-shrink-0 w-80 border-slate-200 hover:border-slate-300 transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
                >
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4 mb-4">
                      <img
                        src={testimonial.photo}
                        alt={testimonial.name}
                        loading="lazy"
                        className="w-16 h-16 rounded-full object-cover border-2 border-slate-200"
                      />
                      <div className="flex-1">
                        <h4 className="font-bold text-slate-900">
                          {testimonial.name}
                        </h4>
                        <p className="text-sm text-slate-600">{testimonial.position}</p>
                        <p className="text-sm text-slate-500">{testimonial.company}</p>
                      </div>
                      <Quote className="w-6 h-6 text-slate-300" />
                    </div>
                    <p className="text-slate-700 leading-relaxed italic">"{testimonial.testimonial}"</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
